// chatContext/chatThreadKey.ts
//
// Which thread a surface anchor resolves to (ORA-505): the server's anchor
// shape for `GET /conversations/anchor`, and the key the chat slice records
// the resolved target under (`threadTargetKey`). Kept apart from
// `useChatConversations` so the always-loaded document viewer provider can
// name a thread without pulling the companion's history code into the app
// shell.

import type { ChatConversationAnchor } from '@/services/chatConversations';
import type { ChatAnchorInput, ChatCourseContext } from '@/types/chatEnvelope';

/** An exam, or no anchor, is the generic thread. */
export const toConversationAnchor = (
  anchor: ChatAnchorInput | null
): ChatConversationAnchor => {
  if (!anchor || anchor.type === 'exam') return { type: 'generic' };

  switch (anchor.type) {
    case 'question':
      return {
        type: 'question',
        id: anchor.vignetteId,
        ...(anchor.variantId ? { variantId: anchor.variantId } : {}),
      };
    case 'flashcard':
      return { type: 'flashcard', id: anchor.flashcardId };
    case 'video':
      return { type: 'video', id: anchor.videoId };
    case 'article':
      return { type: 'article', id: anchor.articleId };
    case 'lecture':
      return { type: 'lecture', id: anchor.lectureId };
  }
};

export const conversationAnchorKey = (anchor: ChatConversationAnchor): string =>
  `${anchor.type}:${'id' in anchor ? anchor.id : ''}:${
    'variantId' in anchor ? (anchor.variantId ?? '') : ''
  }`;

/**
 * The thread a surface resolves to. In a course session the module is the
 * thread (ORA-1055): one continuous conversation across every step and item,
 * while the item's anchor is still published as context. Only the session
 * publishes a course context with a module, and the server keys a turn to the
 * module on the same condition (route `course.session` with `moduleId`).
 */
export const toSurfaceConversationAnchor = (
  anchor: ChatAnchorInput | null,
  course: ChatCourseContext | null
): ChatConversationAnchor =>
  course?.moduleId
    ? { type: 'course_module', id: course.moduleId }
    : toConversationAnchor(anchor);

/** The `threadTargetKey` a surface's published context resolves under. */
export const chatThreadKey = (inputs: {
  surfaceAnchor: ChatAnchorInput | null;
  course: ChatCourseContext | null;
}): string =>
  conversationAnchorKey(
    toSurfaceConversationAnchor(inputs.surfaceAnchor, inputs.course)
  );
